import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getElementStatsByIdentifier } from "core-integration/src/store/element-stats/reducers";
import { IElementStat } from "core-integration/src/store/element-stats/types";
import {
  getElementsById,
  getElementsEventDataById,
} from "core-integration/src/store/elements/reducers";
import { showElementSummary } from "core-integration/src/store/elements/thunks";
import { IElementExplain } from "core-integration/src/store/elements/types";
import { getFixturesForEventById } from "core-integration/src/store/fixtures/reducers";
import { IFixture } from "core-integration/src/store/fixtures/types";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import Box from "../Box";
import Button from "../Button";
import Dialog, { DialogButtonItem } from "../Dialog";
import DialogHeading from "../DialogHeading";
import Table from "../Table";

const ExplainHeading = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes[3]};
  text-align: center;
`;

const ExplainScore = styled.span`
  margin: 0 ${({ theme }) => theme.space[1]};
  padding: ${({ theme }) => theme.space[1]};
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
`;

interface IOwnProps {
  closeDialog: () => void;
  elementId: number;
  eventId: number;
}

type Props = IOwnProps & WithTranslation;

const ElementExplainDialog: React.FC<Props> = ({
  closeDialog,
  elementId,
  eventId,
  t,
}) => {
  const dispatch = useDispatch<ThunkDispatch>();

  const elementsById = useSelector(getElementsById);
  const elementsDataById = useSelector((state: RootState) =>
    getElementsEventDataById(state, eventId),
  );
  const fixturesById: Record<string, IFixture> | null = useSelector(
    (state: RootState) => getFixturesForEventById(state, eventId),
  );
  const statsByIdentifier: Record<string, IElementStat> = useSelector(
    getElementStatsByIdentifier,
  );
  const teamsById = useSelector(getTeamsById);

  const showElementDialog = (elementId: number) => {
    dispatch(showElementSummary(elementId));
    closeDialog();
  };

  const renderExplainFixture = (explain: IElementExplain) => {
    const elementData = elementsDataById![elementId];
    const fixture = fixturesById && fixturesById[explain.fixture];
    if (!elementData || !fixture) {
      return null;
    }
    if (!fixture.started) {
      return null;
    }
    return (
      <>
        <Box mb={4}>
          <ExplainHeading>
            {teamsById[fixture.team_h].short_name}
            <ExplainScore>
              {fixture.team_h_score}-{fixture.team_a_score}
            </ExplainScore>
            {teamsById[fixture.team_a].short_name}
          </ExplainHeading>
        </Box>
        <Box mb={4}>
          <Table>
            <thead>
              <tr>
                <th>{t("elementExplainDialog.stat", "Statistic")}</th>
                <th>{t("elementExplainDialog.value", "Value")}</th>
                <th>{t("elementExplainDialog.points", "Points")}</th>
              </tr>
            </thead>
            <tbody>
              {explain.stats.map((stat) => (
                <tr key={stat.identifier}>
                  <td>{statsByIdentifier[stat.identifier].label}</td>
                  <td>{stat.value}</td>
                  <td>{stat.points}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Box>
      </>
    );
  };

  const element = elementsById[elementId];
  if (!element || !fixturesById || !elementsDataById) {
    return null;
  }
  return (
    <Dialog closeDialog={closeDialog}>
      <Dialog.Header closeDialog={closeDialog}>
        <DialogHeading>{`${element.first_name} ${element.second_name}`}</DialogHeading>
      </Dialog.Header>

      <Dialog.Body>
        {elementsDataById[element.id].explain.map((e) => (
          <React.Fragment key={e.fixture}>
            {renderExplainFixture(e)}
          </React.Fragment>
        ))}
        <div>
          <DialogButtonItem>
            <Button
              variant="primary"
              onClick={() => showElementDialog(element.id)}
              width={1}
            >
              {t("elementExplainDialog.info", "View Information")}
            </Button>
          </DialogButtonItem>
        </div>
      </Dialog.Body>
    </Dialog>
  );
};

export default withTranslation()(ElementExplainDialog);
